// shared/supabase/assetMappers.ts
//
// Converts raw Supabase rows (snake_case) into the typed RTLSAsset /
// RescueChamber / AccessPoint shapes that Redux + the 3D scene consume.
//
// Lives in /shared so web and mobile import identical mapping logic — the
// SQL view performs status mapping on the server side, but realtime
// postgres_changes events come from the underlying `personnel` and
// `equipment` tables, which carry the raw TagBoard enums. We replicate the
// CASE expression here to avoid drift.
//
// Status mapping MUST match supabase/init.sql `public.assets` view:
//   personnel.tagged_in  → active
//   personnel.tagged_out → offline
//   personnel.missing    → alert
//   equipment.active     → active
//   equipment.idle       → idle
//   equipment.down       → alert
//   equipment.maintenance → offline
//   any unrecognised     → offline

import type {
  AccessPoint,
  AccessPointStatus,
  AssetStatus,
  ChamberStatus,
  EquipmentSubType,
  PersonnelRole,
  RescueChamber,
  RTLSAsset,
} from '../types/rtlsTypes';

// ── Personnel ──────────────────────────────────────────────────────────────

const PERSONNEL_STATUS_MAP: Record<string, AssetStatus> = {
  tagged_in: 'active',
  tagged_out: 'offline',
  missing: 'alert',
};

function personnelStatus(raw: unknown): AssetStatus {
  if (typeof raw !== 'string') return 'offline';
  return PERSONNEL_STATUS_MAP[raw] ?? 'offline';
}

function narrowPersonnelSubType(raw: unknown): PersonnelRole | undefined {
  if (raw === 'worker' || raw === 'supervisor' || raw === 'emergency_team') {
    return raw;
  }
  return undefined;
}

export function mapPersonnelRowToAsset(row: any): RTLSAsset | null {
  if (!row || typeof row.id !== 'string') return null;
  return {
    id: row.id,
    // employee_id comes from the personnel table; external_id is the alias
    // used by the assets view. Accept either so the mapper works for both
    // realtime postgres_changes payloads (table) and initial SELECTs (view).
    externalId:
      typeof row.employee_id === 'string'
        ? row.employee_id
        : typeof row.external_id === 'string'
        ? row.external_id
        : undefined,
    name: typeof row.name === 'string' ? row.name : row.id,
    type: 'personnel',
    subType: narrowPersonnelSubType(row.subtype),
    status: personnelStatus(row.status),
    position: {
      x: typeof row.x === 'number' ? row.x : 0,
      y: typeof row.y === 'number' ? row.y : 0,
      z: typeof row.z === 'number' ? row.z : 0,
    },
    lastUpdated: row.last_updated
      ? Date.parse(row.last_updated)
      : row.updated_at
      ? Date.parse(row.updated_at)
      : Date.now(),
    level: typeof row.level === 'number' ? row.level : undefined,
  };
}

// ── Equipment ──────────────────────────────────────────────────────────────

const EQUIPMENT_STATUS_MAP: Record<string, AssetStatus> = {
  active: 'active',
  idle: 'idle',
  down: 'alert',
  maintenance: 'offline',
};

function equipmentStatus(raw: unknown): AssetStatus {
  if (typeof raw !== 'string') return 'offline';
  return EQUIPMENT_STATUS_MAP[raw] ?? 'offline';
}

function narrowEquipmentSubType(raw: unknown): EquipmentSubType | undefined {
  if (
    raw === 'truck' ||
    raw === 'loader' ||
    raw === 'drill_rig' ||
    raw === 'rescue_vehicle' ||
    raw === 'other'
  ) {
    return raw;
  }
  return undefined;
}

export function mapEquipmentRowToAsset(row: any): RTLSAsset | null {
  if (!row || typeof row.id !== 'string') return null;
  return {
    id: row.id,
    // asset_id comes from the equipment table; external_id is the view alias.
    externalId:
      typeof row.asset_id === 'string'
        ? row.asset_id
        : typeof row.external_id === 'string'
        ? row.external_id
        : undefined,
    name: typeof row.name === 'string' ? row.name : row.id,
    type: 'equipment',
    subType: narrowEquipmentSubType(row.subtype),
    status: equipmentStatus(row.status),
    position: {
      x: typeof row.x === 'number' ? row.x : 0,
      y: typeof row.y === 'number' ? row.y : 0,
      z: typeof row.z === 'number' ? row.z : 0,
    },
    lastUpdated: row.last_updated
      ? Date.parse(row.last_updated)
      : row.updated_at
      ? Date.parse(row.updated_at)
      : Date.now(),
    level: typeof row.level === 'number' ? row.level : undefined,
  };
}

// ── Rescue chambers ────────────────────────────────────────────────────────

const CHAMBER_STATUSES: ChamberStatus[] = [
  'available',
  'full',
  'near_capacity',
  'maintenance',
];

function narrowChamberStatus(raw: unknown): ChamberStatus {
  if (typeof raw === 'string' && (CHAMBER_STATUSES as string[]).includes(raw)) {
    return raw as ChamberStatus;
  }
  return 'available';
}

export function mapChamberRow(row: any): RescueChamber | null {
  if (!row || typeof row.id !== 'string') return null;
  // Chambers without 3D coords aren't renderable — drop them at the mapper
  // boundary so the scene never receives a (0,0,0) ghost marker.
  if (typeof row.x !== 'number' || typeof row.y !== 'number' || typeof row.z !== 'number') {
    return null;
  }
  return {
    id: row.id,
    mine_id: typeof row.mine_id === 'string' ? row.mine_id : undefined,
    name: typeof row.name === 'string' ? row.name : row.id,
    location: typeof row.location === 'string' ? row.location : '',
    locationDescription:
      typeof row.location_description === 'string' ? row.location_description : undefined,
    capacity: typeof row.capacity === 'number' ? row.capacity : 0,
    currentOccupancy: typeof row.current_occupancy === 'number' ? row.current_occupancy : 0,
    status: narrowChamberStatus(row.status),
    position: { x: row.x, y: row.y, z: row.z },
    level: typeof row.level === 'number' ? row.level : undefined,
  };
}

// ── Access points ──────────────────────────────────────────────────────────

const AP_STATUSES: AccessPointStatus[] = ['active', 'offline', 'fault'];

function narrowAccessPointStatus(raw: unknown): AccessPointStatus {
  if (typeof raw === 'string' && (AP_STATUSES as string[]).includes(raw)) {
    return raw as AccessPointStatus;
  }
  return 'offline';
}

export function mapAccessPointRow(row: any): AccessPoint | null {
  if (!row || typeof row.id !== 'string') return null;
  // APs without coords can't render — skip them.
  if (typeof row.x !== 'number' || typeof row.y !== 'number' || typeof row.z !== 'number') {
    return null;
  }
  return {
    id: row.id,
    locationId: typeof row.location_id === 'string' ? row.location_id : undefined,
    name: typeof row.name === 'string' ? row.name : row.id,
    position: { x: row.x, y: row.y, z: row.z },
    level: typeof row.level === 'number' ? row.level : undefined,
    status: narrowAccessPointStatus(row.status),
    macAddress: typeof row.mac_address === 'string' ? row.mac_address : undefined,
    radiusM: typeof row.radius_m === 'number' ? row.radius_m : undefined,
    mine_id: typeof row.mine_id === 'string' ? row.mine_id : undefined,
    createdAt: row.created_at ? Date.parse(row.created_at) : undefined,
  };
}
